'use client';

// Galexia Memory — governance surface for the long-term memory layer. View the
// synthesized profile, inspect every stored fact (including invalidated ones),
// read the audit trail, and hard-delete a whole scope. Plain-fetch data
// (mirrors the agents/skills/library client convention). Scope tabs: Personal
// plus one tab per non-archived managed agent.

import { useCallback, useEffect, useState } from 'react';
import { Brain } from '@phosphor-icons/react';
import { Toaster, toast } from 'sonner';
import { useDocumentTitle } from '@/lib/use-document-title';
import { cn } from '@/lib/utils';
import { ProfileCard } from '@/components/memory/profile-card';
import { FactsList } from '@/components/memory/facts-list';
import { AuditTrail } from '@/components/memory/audit-trail';
import { DangerZone } from '@/components/memory/danger-zone';
import type { MemoryScopeData, MemoryScopeTab } from '@/components/memory/types';

const PERSONAL_TAB: MemoryScopeTab = { key: 'user', label: 'Personal' };

export function MemoryClient() {
  useDocumentTitle('Memory');

  const [tabs, setTabs] = useState<MemoryScopeTab[]>([PERSONAL_TAB]);
  const [activeScope, setActiveScope] = useState<string>('user');
  const [data, setData] = useState<MemoryScopeData | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [rebuilding, setRebuilding] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Load agent tabs once (Personal is always present).
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch('/api/agents');
        if (!res.ok) return;
        const json = await res.json();
        const agentTabs: MemoryScopeTab[] = (Array.isArray(json.agents) ? json.agents : [])
          .filter((a: { status?: string }) => a.status !== 'archived')
          .map((a: { id: string; name: string; emoji: string | null }) => ({
            key: `agent:${a.id}`,
            label: a.name,
            emoji: a.emoji,
          }));
        setTabs([PERSONAL_TAB, ...agentTabs]);
      } catch {
        // Agent tabs are best-effort — Personal always works.
      }
    })();
  }, []);

  const fetchScope = useCallback(async (scope: string) => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await fetch(`/api/memory?scope=${encodeURIComponent(scope)}`);
      if (!res.ok) throw new Error(`Failed to load memory (${res.status})`);
      const json = (await res.json()) as MemoryScopeData;
      setData(json);
    } catch (err) {
      setData(null);
      setLoadError(err instanceof Error ? err.message : 'Failed to load memory');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchScope(activeScope);
  }, [activeScope, fetchScope]);

  const isUserScope = activeScope === 'user';
  const activeLabel = tabs.find((t) => t.key === activeScope)?.label ?? 'Personal';

  const handleRebuild = async () => {
    setRebuilding(true);
    try {
      const res = await fetch('/api/memory/consolidate', { method: 'POST' });
      const json = await res.json().catch(() => null);
      if (!res.ok) throw new Error(json?.error ?? `Rebuild failed (${res.status})`);
      if (json?.status === 'written') {
        toast.success('Profile rebuilt');
      } else if (json?.status === 'disabled') {
        toast.warning('Memory ingestion is turned off — nothing to consolidate.');
      } else if (json?.status === 'no-facts') {
        toast.info('No facts yet to build a profile from.');
      } else if (json?.status === 'no-durable-facts') {
        toast.info('No durable preferences found to summarize.');
      } else {
        toast.success('Consolidation ran');
      }
      await fetchScope(activeScope);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Rebuild failed');
    } finally {
      setRebuilding(false);
    }
  };

  const handleDeleteScope = async () => {
    setDeleting(true);
    try {
      const res = await fetch('/api/memory/scope', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ scope: activeScope }),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok) throw new Error(json?.error ?? `Delete failed (${res.status})`);
      toast.success(`Cleared all memories in ${activeLabel}`);
      await fetchScope(activeScope);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background p-2 md:p-3">
      <Toaster position="top-center" richColors closeButton />
      <div className="relative flex min-h-[calc(100vh-1.5rem)] flex-col overflow-hidden rounded-2xl border border-border/80 bg-card/40">
        {/* Sticky header */}
        <header className="sticky top-0 z-30 flex items-center gap-4 border-b border-border/60 bg-background/80 px-4 py-3 backdrop-blur-md md:px-6">
          <div className="flex items-center gap-2">
            <Brain weight="duotone" className="size-5 text-violet-400" />
            <div>
              <h1 className="text-sm font-extrabold leading-none tracking-tight">Memory</h1>
              <p className="mt-0.5 text-[11px] text-muted-foreground">
                See, verify, and control what Galexia remembers about you.
              </p>
            </div>
          </div>
        </header>

        {/* Scope tabs */}
        <div className="flex items-center gap-1.5 overflow-x-auto border-b border-border/40 px-4 py-2.5 md:px-6">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveScope(tab.key)}
              className={cn(
                'shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors',
                activeScope === tab.key
                  ? 'bg-violet-500/15 text-violet-300'
                  : 'text-muted-foreground hover:bg-accent/60 hover:text-foreground',
              )}
            >
              {tab.emoji ? `${tab.emoji} ` : ''}
              {tab.label}
            </button>
          ))}
        </div>

        <main className="min-w-0 flex-1 space-y-4 px-4 py-5 md:px-6">
          {loadError && (
            <p className="rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
              {loadError}
            </p>
          )}

          <ProfileCard
            profileText={data?.profileText ?? null}
            updatedAt={data?.profileUpdatedAt ?? null}
            loading={loading}
            rebuilding={rebuilding}
            onRebuild={handleRebuild}
            isUserScope={isUserScope}
          />

          <FactsList
            facts={data?.facts ?? []}
            total={data?.factsTotal ?? 0}
            loading={loading}
            serviceError={Boolean(data?.serviceError)}
          />

          <AuditTrail audit={data?.audit ?? []} loading={loading} />

          <DangerZone scopeLabel={activeLabel} deleting={deleting} onDelete={handleDeleteScope} />
        </main>
      </div>
    </div>
  );
}
